import localforage from 'localforage';
import { action, runInAction } from 'mobx';

import axi from 'src/services/api';
import type { SmsNotificationPhoneResponse } from 'src/services/api/api';

import type { CustomersStateProps } from './state';
import CustomersState, { initialCustomersState } from './state';

/**
 * Initialize the CustomersState.
 * Always use local data first to populate the state and override it with server data if possible.
 */
export const initializeCustomersState = action(
  'initializeCustomersState',
  async () => {
    await fillWithLocalState();
    await fetchCustomers();

    runInAction(() => (CustomersState.initialized = true));
  },
);

/**
 * Fill with local state.
 */
export const fillWithLocalState = action('fillWithLocalState', async () => {
  try {
    const localCustomersState =
      await localforage.getItem<CustomersStateProps>('customersState');

    if (!localCustomersState) return;

    runInAction(() => {
      Object.assign(CustomersState, localCustomersState);
    });
  } catch (error: any) {
    console.warn('could not retrieve local data ', error);
  }
});

/**
 * Fetch customers.
 */
export const fetchCustomers = action('fetchCustomers', async () => {
  const { data: customers } = await axi.getCustomers();

  runInAction(() => (CustomersState.customers = customers));
});

const applySmsNotificationPhone = (
  customerId: number | string,
  data: SmsNotificationPhoneResponse,
) => {
  runInAction(() => {
    const customer = CustomersState.customers.find(
      (item) => item.id === customerId,
    );
    if (customer) {
      customer.smsNotificationPhone = data.smsNotificationPhone;
      customer.smsNotificationPhoneVerifiedAt =
        data.smsNotificationPhoneVerifiedAt;
    }
  });
};

/**
 * Record that a customer does not want SMS notifications.
 */
export const updateSmsNotificationPhone = action(
  'updateSmsNotificationPhone',
  async (customerId: number | string, phone: string) => {
    const { data } = await axi.updateCustomerSmsNotificationPhone({
      customerId,
      phone,
    });
    applySmsNotificationPhone(customerId, data);

    return data.smsNotificationPhone;
  },
);

/**
 * Send a verification code via SMS to the number the customer gave.
 */
export const sendSmsVerificationCode = action(
  'sendSmsVerificationCode',
  async (customerId: number | string, phone: string) => {
    await axi.sendCustomerSmsVerificationCode({ customerId, phone });
  },
);

/**
 * Confirm the SMS number with the received code and store it on the customer.
 */
export const verifySmsVerificationCode = action(
  'verifySmsVerificationCode',
  async (customerId: number | string, code: string) => {
    const { data } = await axi.verifyCustomerSmsVerificationCode({
      code,
      customerId,
    });
    applySmsNotificationPhone(customerId, data);

    return data;
  },
);

/**
 * Resets customers state.
 */
export const resetCustomersState = action('resetCustomers', () => {
  Object.assign(CustomersState, initialCustomersState);
  localforage.removeItem('customersState');
});
